import type { DrawNumberCap } from '@nexbit/types'

/**
 * Ordering and the loss figure for the per-number ceilings table.
 *
 * Pure on purpose: the table is only worth sorting if the order is right, and a function
 * of (rows, key, direction, stake, payout) is something a test can pin without rendering.
 */

export type SortKey = 'number' | 'cap' | 'loss'
export type SortDir = 'asc' | 'desc'
export interface SortState { key: SortKey; dir: SortDir }

/** The order the table opens in — the same one the server returns. */
export const DEFAULT_SORT: SortState = { key: 'number', dir: 'asc' }

/**
 * Where a column starts when it is first pressed. A number reads naturally ascending; a
 * ceiling and a loss are looked at to find the biggest, so those start descending.
 */
export const FIRST_DIR: Record<SortKey, SortDir> = { number: 'asc', cap: 'desc', loss: 'desc' }

/** Press a column: a new one starts in its natural direction, the current one flips. */
export function nextSort(current: SortState, key: SortKey): SortState {
  if (current.key !== key) return { key, dir: FIRST_DIR[key] }
  return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' }
}

/**
 * What the house pays out if this number comes up, on the stake already taken: stake times
 * the bet type's payout rate. The same definition as the "คาดเดายอดเสีย" column of the stake
 * table above it, so the two never show different figures for one number.
 *
 * Gross, not net of the stake collected on the rest of the type — that netting is a
 * property of the whole type (the worst-case line above), not of one number. And at the
 * type's rate, not an agent's reduced one: it is the house's exposure, and an agent that
 * pays less only makes it smaller.
 */
export function lossIfHit(stake: number, payout: number): number {
  return stake * payout
}

/** Compare two numbers as written: digits by value, choice words (high, odd, …) as text. */
function compareNumber(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true })
}

/**
 * The rows in the requested order. Ties — a table of numbers nobody has bet on is all ties
 * on the loss — fall back to the number ascending, so the order is stable and the same
 * from one poll to the next instead of shuffling whatever the sort left equal.
 */
export function sortCaps(
  rows: DrawNumberCap[],
  sort: SortState,
  stakeOf: (c: DrawNumberCap) => number,
  payout: number,
): DrawNumberCap[] {
  const sign = sort.dir === 'asc' ? 1 : -1
  const value = (c: DrawNumberCap): number | null => {
    if (sort.key === 'cap') return Number(c.maxPerNumber)
    if (sort.key === 'loss') return lossIfHit(stakeOf(c), payout)
    return null
  }
  return [...rows].sort((a, b) => {
    const va = value(a)
    const vb = value(b)
    if (va !== null && vb !== null && va !== vb) return sign * (va - vb)
    if (sort.key === 'number') return sign * compareNumber(a.number, b.number)
    return compareNumber(a.number, b.number)
  })
}
